'use client'

import { useEffect, useRef, useState } from 'react'
import { useUser } from '@clerk/nextjs'
import { isClerkAPIResponseError } from '@clerk/nextjs/errors'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2 } from 'lucide-react'
import {
  PASSWORD_REQUIREMENTS_HINT,
  changePasswordSchema,
  type ChangePasswordFormValues,
} from '@/features/auth/schemas/auth.schema'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field'
import { PasswordInput } from '@/components/patterns/passwordInput'

const SUCCESS_MESSAGE_MS = 6000

/**
 * Self-service change-password — no admin/staff approval needed, unlike staff's admin-driven
 * reset (`AdminUsersSyncService.resetPassword`). Uses the classic Clerk `UserResource` API
 * (`useUser()`'s `updatePassword`), distinct from the Future `signIn` API used by the
 * sign-in/forgot-password flows, since this is an already-signed-in user acting on their own
 * account rather than establishing a new session. No `signOutOfOtherSessions` — a deliberate
 * self-service change isn't a compromise-response flow, so other sessions are left alone
 * (unlike the forgot-password path, which does sign them out).
 *
 * Role-agnostic by design — `useUser()` always resolves to whichever account is currently
 * signed in, so this one component is shared verbatim between Investor's `/profile` and
 * Admin's `/admin/profile`, each rendered only inside its own role-guarded layout. No role
 * prop, no branching: there is nothing here that could conflict between the two surfaces.
 */
export function ChangePasswordForm({ onSuccess }: { onSuccess?: () => void } = {}) {
  const { user } = useUser()
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (successTimer.current) clearTimeout(successTimer.current)
    }
  }, [])

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onTouched',
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    if (!user) return
    setSubmitting(true)
    setErrorMessage(null)
    setSuccessMessage(null)
    if (successTimer.current) clearTimeout(successTimer.current)
    try {
      await user.updatePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      form.reset({ currentPassword: '', newPassword: '', confirmNewPassword: '' })
      setSuccessMessage('Your password has been updated.')
      successTimer.current = setTimeout(() => setSuccessMessage(null), SUCCESS_MESSAGE_MS)
      onSuccess?.()
    } catch (err) {
      // Two independent signals — the confirmed `form_password_incorrect` code, and the
      // `current_password` param name Clerk attaches to this specific field error — since
      // this couldn't be verified against a live account from here. If neither matches,
      // the dev console log below is what lets a real failure be diagnosed precisely next
      // time, instead of guessing at the shape again.
      const wrongCurrentPassword =
        isClerkAPIResponseError(err) &&
        err.errors.some(
          (e) => e.code === 'form_password_incorrect' || e.meta?.paramName === 'current_password',
        )
      if (!wrongCurrentPassword && process.env.NODE_ENV !== 'production') {
        console.error(
          'ChangePasswordForm: unrecognized updatePassword error shape',
          isClerkAPIResponseError(err) ? err.errors : err,
        )
      }
      setErrorMessage(
        wrongCurrentPassword
          ? 'Your current password is incorrect.'
          : 'Something went wrong. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor="current-password">Current password</FieldLabel>
        <PasswordInput
          id="current-password"
          placeholder="Enter your current password"
          aria-invalid={!!form.formState.errors.currentPassword}
          {...form.register('currentPassword')}
        />
        <FieldError errors={[form.formState.errors.currentPassword]} />
      </Field>
      <Field>
        <FieldLabel htmlFor="new-password">New password</FieldLabel>
        <PasswordInput
          id="new-password"
          placeholder="Create a new password"
          aria-invalid={!!form.formState.errors.newPassword}
          {...form.register('newPassword')}
        />
        <FieldError errors={[form.formState.errors.newPassword]} />
        <FieldDescription>{PASSWORD_REQUIREMENTS_HINT}</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="confirm-new-password">Confirm new password</FieldLabel>
        <PasswordInput
          id="confirm-new-password"
          placeholder="Re-enter your new password"
          aria-invalid={!!form.formState.errors.confirmNewPassword}
          {...form.register('confirmNewPassword')}
        />
        <FieldError errors={[form.formState.errors.confirmNewPassword]} />
      </Field>
      {errorMessage && <FieldDescription className="text-red-600">{errorMessage}</FieldDescription>}
      {successMessage && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-lg bg-[var(--background-success-primary)] px-3 py-2 text-sm font-medium text-[var(--text-success)]"
        >
          <CheckCircle2 className="size-4 shrink-0" />
          {successMessage}
        </div>
      )}
      <Field>
        <Button type="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Updating…' : 'Update password'}
        </Button>
      </Field>
    </FieldGroup>
  )
}
